import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import { makeWeldJoint, passVec2 } from "../box2dUtils";
import { Transform, Physics, VirusPointAttractor, Collision, Targeting, RangeType, Virus, Chained, SuspendMovement, Ownership } from "../components";
import { getEntityPlayer } from "../composition";
import { CollisionCategory, Speeds } from "../constants";
import { getId } from "../entityFactory";
import { PerfTracker } from "../performanceTracking";
import { getOrAddComponent, printObjectProperties, rotateAround, vecToStr } from "../utils";

const RANGE = 60;

export class AttractorSystem extends System {
	execute() {
		PerfTracker.startSubsystem('Creating attractors');
		this.queries.attractors.added?.forEach(entity => {
			const attractor = entity.getComponent(VirusPointAttractor);
			const transform = entity.getComponent(Transform);

			const up = new vec2([0, 1]);
			const p1 = up.scale(transform.size.y / 2);
			p1.add(transform.position);
			attractor.attractorPoint.xy = p1.xy;

			const targeting = getOrAddComponent(entity, Targeting);
			const targetIdx = targeting.addSpec(RangeType.Circle, RANGE, CollisionCategory.Virus | CollisionCategory.Virophage, 0, false, false, 0);
			attractor.targetIdx = targetIdx;
		});
		PerfTracker.endSubsystem();

		//Attractors pull nearby attractors towards themselves
		PerfTracker.startSubsystem('Attractor pull');
		this.queries.attractors.results.forEach(entity => {
			const attractor = entity.getComponent(VirusPointAttractor);
			const physics = entity.getComponent(Physics);

			const suspended = entity.hasComponent(SuspendMovement);
			if (suspended) return;;

			// //Make sure joined entities are still alive
			for (const joined of attractor.joinedEntities) {
				if (getId(joined) === null) {
					attractor.joinedEntities.delete(joined);
				}
			}

			if (attractor.disabled && attractor.joinedEntities.size === 0) {
				this.enableVirusCollision(physics);
				attractor.disabled = false;
				const targetting = entity.getComponent(Targeting);
				if (targetting) {
					targetting.enableSpec(attractor.targetIdx);
				}
			}

			if (attractor.disabled) return;

			const ownership = entity.getComponent(Ownership);
			const ownerId = ownership && ownership.playerId || -1;

			const transform = entity.getComponent(Transform);
			const targeting = entity.getComponent(Targeting);
			const virusesInRange = targeting.getTargets(attractor.targetIdx);

			//Update attractor point
			attractor.attractorPoint.xy = [0, 1];
			attractor.attractorPoint.scale(transform.size.y / 2);
			attractor.attractorPoint.add(transform.position);

			//Rotate a little bit
			physics.body.ApplyAngularImpulse(4, true);

			//vec2 cache
			const force = new vec2();

			// Note: I removed the sort by distance here because its costly with large numbers of viruses, and I think the swarm behavior
			// works fine without it. If swarm behavior is not good enough, we can add it back.
			virusesInRange
				.filter(e => e.hasComponent(Transform))
			// .sort((a, b) => { 
			// 	const aTransform = a.getComponent(Transform);
			// 	const bTransform = b.getComponent(Transform);
			// 	const aDistance = vec2.squaredDistance(aTransform.position, transform.position);
			// 	const bDistance = vec2.squaredDistance(bTransform.position, transform.position);
			// 	return aDistance - bDistance;
			// });

			let maxAttractees = 8;
			for (const otherEntity of virusesInRange) {
				if (otherEntity === entity) continue;
				if (otherEntity.hasComponent(SuspendMovement)) continue;

				if (maxAttractees < 0) break;
				--maxAttractees;

				const otherTransform = otherEntity.getComponent(Transform);
				const otherPhysics = otherEntity.getComponent(Physics);
				if (!otherPhysics) continue;

				const otherOwnership = otherEntity.getComponent(Ownership);
				const otherOwnerId = otherOwnership && otherOwnership.playerId || -1;
				if (otherOwnerId !== ownerId) continue;

				const distance = vec2.distance(attractor.attractorPoint, otherTransform.position);

				if (distance >= transform.size.x / 2) {
					vec2.direction(attractor.attractorPoint, otherTransform.position, force).scale(Speeds.Fast, force);
					physics.body.ApplyForce(passVec2(force.negate()), passVec2(attractor.attractorPoint), true);
				}
			}
		});
		PerfTracker.endSubsystem();

		//Collisions with other viruses
		PerfTracker.startSubsystem('Attractor collisions');
		this.queries.collisions.added?.forEach(entity => {
			const collision = entity.getComponent(Collision);
			const physics = entity.getComponent(Physics);
			const attractor = entity.getComponent(VirusPointAttractor);
			const hits = collision.collidedWith;

			const ownership = entity.getComponent(Ownership);
			const ownerId = ownership && ownership.playerId || -1;

			for (const hit of hits) {
				if (hit.canDamage || !hit.entity.hasComponent(Virus) || attractor.joinedEntities.has(hit.entity)) { continue; }

				const otherAttractor = hit.entity.getComponent(VirusPointAttractor);
				if (otherAttractor && otherAttractor.joinedEntities.has(entity)) { continue; }

				const hitOwnership = hit.entity.getComponent(Ownership);
				const hitOwnerId = hitOwnership && hitOwnership.playerId || -1;
				if (ownerId !== hitOwnerId) continue;

				const hitPhysics = hit.entity.getComponent(Physics);
				makeWeldJoint(hitPhysics.body, physics.body); //note that joints disconnect on body death automatically

				//Mark entities as Chained so sync system will sync them together
				const myLinks = getOrAddComponent(entity, Chained);
				const theirLinks = getOrAddComponent(hit.entity, Chained);
				if (!myLinks.links.includes(hit.entity)) myLinks.links.push(hit.entity);
				if (!theirLinks.links.includes(entity)) theirLinks.links.push(entity);

				//Prevent further collision
				attractor.joinedEntities.add(hit.entity);
				this.disableVirusColision(physics);
				attractor.disabled = true;
				const targetting = entity.getComponent(Targeting);
				if (targetting) {
					targetting.disableSpec(attractor.targetIdx);
				}
			}
		});
		PerfTracker.endSubsystem();
	}

	disableVirusColision(physics: Physics) {
		const filter = physics.fixture.GetFilterData();
		filter.set_categoryBits(CollisionCategory.Virus);
		physics.fixture.SetFilterData(filter);
	}

	//Note, when I try to use this in "added" up above physics acts wild. Not sure why. Seems more stable when initialized to "virophage" in physics system
	enableVirusCollision(physics: Physics) {
		const filter = physics.fixture.GetFilterData();
		filter.set_categoryBits(CollisionCategory.Virophage);
		physics.fixture.SetFilterData(filter);
	}
}

AttractorSystem.queries = {
	attractors: { components: [Transform, Physics, VirusPointAttractor], listen: { added: true, removed: true } },
	collisions: { components: [Transform, Physics, VirusPointAttractor, Collision], listen: { added: true } },
};
